import React, { useMemo, useState } from 'react';
import { Search, Download, FileText, ShieldCheck, Ban, Trash2, Archive, FilePlus2, Pencil, Printer, CheckCircle2, Activity } from 'lucide-react';
import type { AppState } from '../types';
import { PageShell, glassLight, inputStyle, ghostButton, EmptyState, StatCard, usePagination, Pagination, useIsMobile, T } from '../ui';
import { csvCell, downloadText } from '../storage';

// Cada evento se guarda como una sola cadena: `${fecha} - ${mensaje}`
// (ver useAuditoria en db.ts). Aquí se parte en sus dos partes para poder
// mostrarlas por separado, agrupar por día y filtrar.
function partir(evento: string): {
  fecha: string;
  mensaje: string;
} {
  const corte = evento.indexOf(' - ');
  if (corte === -1) return {
    fecha: '',
    mensaje: evento
  };
  return {
    fecha: evento.slice(0, corte).trim(),
    mensaje: evento.slice(corte + 3).trim()
  };
}

// El día es lo que va antes de la coma del sello de fecha ("27 jul, 02:31 p. m.").
function diaDe(fecha: string): string {
  const coma = fecha.indexOf(',');
  return coma === -1 ? fecha : fecha.slice(0, coma).trim();
}
function horaDe(fecha: string): string {
  const coma = fecha.indexOf(',');
  return coma === -1 ? '' : fecha.slice(coma + 1).trim();
}

// ── CLASIFICACIÓN DE ACCIONES ───────────────────────────────────────────────
// Los mensajes son texto libre generado por los módulos, así que el tipo se
// deduce del verbo. El orden importa: "desarchivado" tiene que probarse
// antes que "archivado", y "anulada" antes que el resto.
type Tipo = {
  id: string;
  label: string;
  color: string;
  soft: string;
  icono: React.ElementType;
  prueba: RegExp;
};
const TIPOS: Tipo[] = [{
  id: 'autorizacion',
  icono: ShieldCheck,
  label: 'Autorización',
  color: T.ok,
  soft: T.okSoft,
  prueba: /autorizad/i
}, {
  id: 'anulacion',
  icono: Ban,
  label: 'Anulación',
  color: T.danger,
  soft: T.dangerSoft,
  prueba: /anulad/i
}, {
  id: 'eliminacion',
  icono: Trash2,
  label: 'Eliminación',
  color: T.danger,
  soft: T.dangerSoft,
  prueba: /eliminad/i
}, {
  id: 'archivo',
  icono: Archive,
  label: 'Archivo',
  color: T.warn,
  soft: T.warnSoft,
  prueba: /archivad|desarchivad/i
}, {
  id: 'creacion',
  icono: FilePlus2,
  label: 'Creación',
  color: T.ok,
  soft: T.okSoft,
  prueba: /cread[oa]|registrad[oa]/i
}, {
  id: 'edicion',
  icono: Pencil,
  label: 'Edición',
  color: T.info,
  soft: T.infoSoft,
  prueba: /editad[oa]|actualizad[oa]/i
}, {
  id: 'impresion',
  icono: Printer,
  label: 'Impresión',
  color: T.textSecondary,
  soft: T.surfaceSubtle,
  prueba: /impres[oa]/i
}, {
  id: 'resolucion',
  icono: CheckCircle2,
  label: 'Resolución',
  color: T.info,
  soft: T.infoSoft,
  prueba: /resuelt[oa]/i
}];
const OTRO: Tipo = {
  id: 'otro',
  icono: Activity,
  label: 'Actividad',
  color: T.textSecondary,
  soft: T.surfaceSubtle,
  prueba: /.^/
};
function tipoDe(mensaje: string): Tipo {
  return TIPOS.find(t => t.prueba.test(mensaje)) ?? OTRO;
}

// La entidad es la primera palabra del mensaje ("Usuario creado: Juan"), salvo
// los mensajes de varias palabras que ya conocemos.
function entidadDe(mensaje: string): string {
  if (/^orden de pedido/i.test(mensaje)) return 'Orden de pedido';
  return mensaje.split(/[\s:]/)[0] || '—';
}
interface EventoParseado {
  crudo: string;
  fecha: string;
  dia: string;
  hora: string;
  mensaje: string;
  tipo: Tipo;
  entidad: string;
}
export function Auditoria({
  state
}: {
  state: AppState;
}) {
  const isMobile = useIsMobile();
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  // `state.audit` ya viene con el más reciente primero.
  const eventos: EventoParseado[] = useMemo(() => {
    const lista = Array.isArray(state.audit) ? state.audit : [];
    return lista.map(e => {
      const crudo = String(e);
      const {
        fecha,
        mensaje
      } = partir(crudo);
      return {
        crudo,
        fecha,
        dia: diaDe(fecha),
        hora: horaDe(fecha),
        mensaje,
        tipo: tipoDe(mensaje),
        entidad: entidadDe(mensaje)
      };
    });
  }, [state.audit]);

  // Solo se ofrecen como filtro los tipos que realmente aparecen en el log.
  const tiposPresentes = useMemo(() => {
    const vistos = new Set(eventos.map(e => e.tipo.id));
    return [...TIPOS, OTRO].filter(t => vistos.has(t.id));
  }, [eventos]);
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return eventos.filter(e => filtroTipo === 'todos' || e.tipo.id === filtroTipo).filter(e => !q || e.crudo.toLowerCase().includes(q));
  }, [eventos, busqueda, filtroTipo]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados);

  // Día más reciente del log: sirve para destacar "lo de hoy" sin depender de
  // la fecha real del navegador, porque el sello guardado no trae año.
  const diaReciente = eventos[0]?.dia ?? '';
  const stats = useMemo(() => ({
    total: eventos.length,
    recientes: eventos.filter(e => e.dia === diaReciente).length,
    cambios: eventos.filter(e => ['creacion', 'edicion', 'eliminacion', 'archivo'].includes(e.tipo.id)).length
  }), [eventos, diaReciente]);
  const exportar = () => {
    const filas = [['Fecha', 'Tipo', 'Entidad', 'Detalle'], ...filtrados.map(e => [e.fecha, e.tipo.label, e.entidad, e.mensaje])];
    const csv = filas.map(f => f.map(csvCell).join(',')).join('\n');
    // BOM para que Excel abra bien los acentos.
    downloadText('auditoria.csv', '﻿' + csv, 'text/csv;charset=utf-8');
  };

  // Los eventos de la página se agrupan por día, conservando el orden.
  const grupos = useMemo(() => {
    const salida: {
      dia: string;
      items: EventoParseado[];
    }[] = [];
    for (const e of pageItems) {
      const ultimo = salida[salida.length - 1];
      if (ultimo && ultimo.dia === e.dia) ultimo.items.push(e);else salida.push({
        dia: e.dia,
        items: [e]
      });
    }
    return salida;
  }, [pageItems]);
  return <PageShell title="Auditoría" sub="Registro de acciones y cambios del sistema" action={eventos.length > 0 ? <button onClick={exportar} style={ghostButton} data-id="element-151" data-mp="2xabymb06ox5f">
            <Download size={14} data-id="element-152" data-mp="2xau5e1kh2trl" /> Exportar CSV
          </button> : undefined} data-id="element-150" data-mp="2sjlvz5j578ef">

      <style data-id="element-153" data-mp="328dkf3ozvakl">{`
        .aud-dia { position: sticky; top: 0; z-index: 2; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
        .aud-row { position: relative; display: grid; align-items: start; transition: background .15s; }
        .aud-row:hover { background: ${T.surfaceHover}; }
        /* Riel vertical que une los marcadores. Se recorta en el primero y
           el ultimo de cada dia para que no sobresalga del grupo. */
        .aud-row::before { content: ''; position: absolute; left: var(--riel); top: 0; bottom: 0; width: 1px; background: ${T.border}; }
        .aud-row:first-of-type::before { top: 26px; }
        .aud-row:last-of-type::before { bottom: calc(100% - 26px); }
        .aud-mark { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0; }
      `}</style>

      {eventos.length === 0 ? <EmptyState title="Sin eventos registrados" hint="Las acciones y cambios del sistema aparecerán aquí en cuanto ocurran." icon={<FileText size={22} data-id="element-155" data-mp="14k069slolhr6" />} data-id="element-154" data-mp="19j34ou75opq6" /> : <>
          <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0,1fr))',
        gap: 14,
        marginBottom: 22
      }} data-id="element-156" data-mp="znt142140aaa">

            <StatCard label="Eventos" value={String(stats.total)} note="en el registro" data-id="element-157" data-mp="qljrdedo0kxy" />

            <StatCard label={diaReciente ? `Actividad del ${diaReciente}` : 'Último día'} value={String(stats.recientes)} note="eventos" accent={T.info} data-id="element-158" data-mp="ln8gr1fjq6eo" />

            <StatCard label="Cambios de datos" value={String(stats.cambios)} note="altas, ediciones y archivos" accent={T.warn} data-id="element-159" data-mp="2kzbad4hvwh62" />

          </div>

          {/* ── FILTROS ── */}
          <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 14
      }} data-id="element-160" data-mp="1owypg0dx115r">

            <div style={{
          position: 'relative',
          flex: '1 1 240px'
        }} data-id="element-161" data-mp="1twtgc3xoxqqt">

              <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-162" data-mp="1f3rnkpidw3af" />

              <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar en el registro" value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-163" data-mp="167io5auep4oa" />

            </div>
          </div>

          <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 18
      }} data-id="element-164" data-mp="115jjzyque3qa">

            {[{
          id: 'todos',
          label: 'Todos',
          color: T.accent,
          soft: T.accentSoft
        }, ...tiposPresentes].map(t => {
          const activo = filtroTipo === t.id;
          return <button key={t.id} type="button" onClick={() => setFiltroTipo(t.id)} style={{
            padding: '6px 13px',
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: activo ? t.color : T.textSecondary,
            background: activo ? t.soft : 'transparent',
            border: `1px solid ${activo ? `${t.color}44` : T.border}`,
            transition: 'all .15s'
          }} data-id="element-165" data-mp="jxa5s5h5kff4">

                    {t.label}
                  </button>;
        })}
          </div>

          {filtrados.length === 0 ? <EmptyState title="Ningún evento coincide" hint="Prueba con otro texto o quita el filtro de tipo." icon={<Search size={22} data-id="element-167" data-mp="17kie6w9vo4ld" />} data-id="element-166" data-mp="1cjbpqj5ltliw" /> : <div style={{
        ...glassLight,
        overflow: 'hidden'
      }} data-id="element-168" data-mp="3ecj9c4s92pal">

              {grupos.map(grupo => <div key={grupo.dia} data-id="element-169" data-mp="39e7zhu1ygesd">
                  <div className="aud-dia" style={{
            padding: '10px 18px',
            background: 'rgba(255,252,248,.86)',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 10
          }} data-id="element-170" data-mp="39f9bc0qsp5b5">

                    <span style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: T.text
            }} data-id="element-171" data-mp="1cnnl5zhn0mbq">

                      {grupo.dia || 'Sin fecha'}
                    </span>
                    <span style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-172" data-mp="24h67j5a5fgjv">

                      {grupo.items.length}{' '}
                      {grupo.items.length === 1 ? 'evento' : 'eventos'}
                    </span>
                  </div>

                  {grupo.items.map((e, i) => {
            const Icono = e.tipo.icono;
            return <div key={`${grupo.dia}-${i}`} className="aud-row" style={{
              // El riel se alinea con el centro del marcador.
              ['--riel' as string]: isMobile ? '33px' : '89px',
              gridTemplateColumns: isMobile ? '18px 30px minmax(0,1fr)' : '74px 30px minmax(0,1fr)',
              gap: 14,
              padding: '13px 18px'
            }} data-id="element-173" data-mp="9v1kykh0ni8m" data-mpi={i}>

                        {isMobile ? <span data-id="element-174" data-mp="stxdzsh51qkv" /> : <span style={{
                fontSize: 12,
                color: T.textMuted,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                paddingTop: 7,
                textAlign: 'right'
              }} data-id="element-175" data-mp="su5qhj9cmtvj">

                            {e.hora || '—'}
                          </span>}

                        <span className="aud-mark" title={e.tipo.label} style={{
                background: e.tipo.soft,
                border: `1px solid ${e.tipo.color}33`,
                color: e.tipo.color
              }} data-id="element-176" data-mp="16kiy1kibw6kc">

                          <Icono size={14} data-id="element-177" data-mp="3cmmqbwjype6t" />
                        </span>

                        <div style={{
                minWidth: 0,
                paddingTop: 4
              }} data-id="element-178" data-mp="3ckk0pdmq1n2m">
                          <div style={{
                  fontSize: 13.5,
                  color: T.text,
                  lineHeight: 1.45,
                  fontWeight: 600,
                  wordBreak: 'break-word'
                }} data-id="element-179" data-mp="3mcfz6pspzf7d">

                            {e.mensaje}
                          </div>
                          <div style={{
                  marginTop: 3,
                  fontSize: 11,
                  color: T.textMuted,
                  display: 'flex',
                  gap: 7,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }} data-id="element-180" data-mp="3rbsvs29e80n5">

                            <span style={{
                    color: e.tipo.color,
                    fontWeight: 700
                  }} data-id="element-181" data-mp="2ax127m0ignc9">
                              {e.tipo.label}
                            </span>
                            <span data-id="element-182" data-mp="lfvv6d26yijl">·</span>
                            <span data-id="element-183" data-mp="li8k5a6d9t65">{e.entidad}</span>
                            {isMobile && e.hora ? <>
                                <span data-id="element-184" data-mp="qh30m5gv35n3">·</span>
                                <span data-id="element-185" data-mp="br4221le9s73">{e.hora}</span>
                              </> : null}
                          </div>
                        </div>
                      </div>;
          })}
                </div>)}

              <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-186" data-mp="2ouvfmt8yiyz">
                <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="eventos" data-id="element-187" data-mp="7pa8sedj3qgy" />

              </div>
            </div>}
        </>}
    </PageShell>;
}